/**
 * Shared (client + server) construction of a ticket's unified cross-channel conversation
 * timeline (PRD v4.0 §11.4): each linked ChannelMessage contributes the inbound customer
 * message and (if present) the agent/AI reply; each IssueComment contributes a staff note
 * (internal) or customer-facing comment. All interleaved chronologically across channels.
 *
 * One definition consumed by BOTH the ticket-detail screen (IzzyUI) and the Export Ticket PDF
 * action (IzzyActions), so an exported PDF can never drift from what the screen shows.
 *
 * Inputs are structural ("Like") types rather than concrete entity classes: the client passes
 * ChannelMessageEntityExtendedClient / mjBizAppsIssuesIssueCommentEntity, the server passes
 * their server-side counterparts, and this package needs no dependency on the bizapps entity
 * packages.
 */

/** A single chronological entry in the unified cross-channel timeline. */
export interface TicketTimelineEntry {
    /**
     * inbound  — a customer message into the ticket
     * reply    — an AI/agent reply generated on a message
     * note     — a staff INTERNAL note: a ticket-level IssueComment (Source='internal') OR a
     *            message-level ChannelMessageNote (see noteOnMessageAt). Never customer-facing.
     * comment  — a staff customer-facing comment (IssueComment Source='outbound'/'inbound')
     */
    kind: 'inbound' | 'reply' | 'note' | 'comment';
    /** Sort key — the message received time (inbound) or its created time (reply/comment). */
    when: Date;
    /** Channel display name, e.g. "support@rasa.io". */
    channelName: string;
    /** Channel type name (Email, SMS) — drives the icon. */
    channelType: string;
    /** Customer sender (inbound), agent/AI label (reply), or author name (note/comment). */
    who: string;
    /** Rendered body (HTML/plain). */
    content: string;
    /** Approval state for an AI/agent reply, when this entry is a reply. */
    approvalStatus?: string | null;
    /** ChannelMessage ID backing this reply entry (lets the timeline approve/reject the pending AI reply in-place). */
    messageId?: string;
    /** Outbound audit: "To a@x.com · Cc b@y.com" from SentToRecipients/SentCCRecipients. */
    sentRecipients?: string;
    /** Outbound type marker: 'Forward' renders as a forward event rather than a reply. */
    outboundType?: string | null;
    /**
     * Set only for a MESSAGE-level note (ChannelMessageNote): when the message it was written on
     * was received, so the ticket view and PDF can tag it "on message from <date>". Message notes
     * flow one way into the ticket timeline; ticket notes never flow back to messages.
     */
    noteOnMessageAt?: Date | null;
}

/** Channel display info a caller resolves per ChannelID (name + channel type name). */
export interface TimelineChannelInfo {
    name: string;
    type: string;
}

/** The ChannelMessage fields the timeline reads — satisfied by client and server entity classes alike. */
export interface TimelineMessageLike {
    readonly ID: string;
    readonly ChannelID: string;
    readonly Sender: string | null;
    readonly Recipient: string | null;
    readonly MessageContent: string | null;
    readonly SentContent: string | null;
    readonly GeneratedReplyContent: string | null;
    readonly ApprovedReplyContent: string | null;
    readonly ApprovalStatus: string | null;
    readonly ReceivedAt: Date | null;
    readonly SentAt: Date | null;
    readonly ApprovedAt: Date | null;
    readonly OutboundType: string | null;
    readonly SentToRecipients: string | null;
    readonly SentCCRecipients: string | null;
    readonly __mj_CreatedAt: Date;
}

/** Author attribution fields shared by IssueComment and ChannelMessageNote rows. */
export interface TimelineAuthoredLike {
    readonly ID: string;
    readonly AuthorEmail: string | null;
    readonly AuthorPersonID: string | null;
}

/** The IssueComment fields the timeline reads. */
export interface TimelineCommentLike extends TimelineAuthoredLike {
    readonly Source: string;
    readonly Body: string | null;
    readonly __mj_CreatedAt: Date;
}

/** The ChannelMessageNote fields the timeline reads (staff-only note on ONE message). */
export interface TimelineMessageNoteLike extends TimelineAuthoredLike {
    readonly ChannelMessageID: string;
    readonly Body: string | null;
    readonly __mj_CreatedAt: Date;
}

/**
 * Formats the outbound recipient audit line ("To a@x.com · Cc b@y.com") from the sent
 * recipient columns. Undefined when the message predates recipient auditing (renders nothing).
 * Exported: the ticket screen's activity tab renders the same audit line per sent event.
 */
export function FormatSentRecipients(msg: TimelineMessageLike): string | undefined {
    if (!msg.SentAt || !msg.SentToRecipients) return undefined;
    const parts = [`To ${msg.SentToRecipients}`];
    if (msg.SentCCRecipients) parts.push(`Cc ${msg.SentCCRecipients}`);
    return parts.join(' · ');
}

/**
 * Builds the unified timeline from a ticket's linked messages and comments.
 *
 * @param messages       The ticket's ChannelMessages (any order — result is re-sorted).
 * @param comments       The ticket's IssueComments.
 * @param messageNotes   Staff notes written on the ticket's individual messages
 *                       (ChannelMessageNote). Each renders as an internal note tagged with the
 *                       message it belongs to. Callers pass [] to omit (e.g. a PDF export without
 *                       internal notes).
 * @param resolveChannel Resolves a ChannelID to display name + channel type name
 *                       (callers typically back this with IzzyMetadataEngine).
 * @param commentAuthor  Optional display-name resolver for a comment's or note's author (e.g. the
 *                       ticket screen's Person lookup); falls back to AuthorEmail, then 'Staff'.
 */
export function BuildTicketTimeline(
    messages: ReadonlyArray<TimelineMessageLike>,
    comments: ReadonlyArray<TimelineCommentLike>,
    messageNotes: ReadonlyArray<TimelineMessageNoteLike>,
    resolveChannel: (channelId: string) => TimelineChannelInfo,
    commentAuthor?: (authored: TimelineAuthoredLike) => string | undefined,
): TicketTimelineEntry[] {
    const entries: TicketTimelineEntry[] = [];

    for (const msg of messages) {
        const { name: channelName, type: channelType } = resolveChannel(msg.ChannelID);

        // A staff PENDING draft (Manual Messages, #289): OutboundType stamped at creation, not
        // sent yet — no inbound half, no SentContent. Render as a pending staff reply, never a
        // phantom empty inbound.
        const isStaffPendingDraft = !msg.MessageContent && !msg.SentContent &&
            !!msg.OutboundType && msg.OutboundType !== 'Forward';
        if (isStaffPendingDraft) {
            entries.push({
                kind: 'reply',
                when: msg.__mj_CreatedAt,
                channelName,
                channelType,
                who: 'Staff',
                content: msg.ApprovedReplyContent || '',
                sentRecipients: `Draft — pending approval, to ${msg.Recipient}`,
                outboundType: msg.OutboundType,
            });
            continue;
        }

        // A staff outbound reply (Send Ticket Reply) is a ChannelMessage with SentContent but NO
        // MessageContent (no inbound half) — render it as a single outbound reply entry, not a phantom
        // inbound customer message.
        const isOutboundOnly = !msg.MessageContent && !!msg.SentContent;
        if (isOutboundOnly) {
            entries.push({
                kind: 'reply',
                when: msg.SentAt || msg.__mj_CreatedAt,
                channelName,
                channelType,
                who: 'Staff',
                content: msg.SentContent || '',
                sentRecipients: FormatSentRecipients(msg),
                outboundType: msg.OutboundType,
            });
            continue;
        }

        entries.push({
            kind: 'inbound',
            when: msg.ReceivedAt || msg.__mj_CreatedAt,
            channelName,
            channelType,
            who: msg.Sender || 'Customer',
            content: msg.MessageContent || '',
        });

        const reply = msg.ApprovedReplyContent || msg.GeneratedReplyContent;
        if (reply) {
            entries.push({
                kind: 'reply',
                when: msg.ApprovedAt || msg.__mj_CreatedAt,
                channelName,
                channelType,
                who: 'Izzy',
                content: reply,
                approvalStatus: msg.ApprovalStatus,
                messageId: msg.ID,
                sentRecipients: FormatSentRecipients(msg),
            });
        }
    }

    for (const c of comments) {
        // Source 'internal' = staff-only note; 'outbound'/'inbound' = customer-facing comment.
        const isInternal = c.Source === 'internal';
        entries.push({
            kind: isInternal ? 'note' : 'comment',
            when: c.__mj_CreatedAt,
            channelName: '',
            channelType: '',
            who: commentAuthor?.(c) || c.AuthorEmail || 'Staff',
            content: c.Body || '',
        });
    }

    // Message-level notes: same internal-note treatment, tagged with the message they were
    // written on (its received time; falls back to the message's created time for staff-
    // originated rows that were never "received"). A note whose message isn't in this ticket's
    // message set (moved by a merge, or a caller passing a wider set) is skipped rather than
    // rendered unanchored.
    const receivedByMessageId = new Map<string, Date>();
    for (const msg of messages) {
        receivedByMessageId.set(msg.ID, msg.ReceivedAt || msg.__mj_CreatedAt);
    }
    for (const n of messageNotes) {
        const onMessageAt = receivedByMessageId.get(n.ChannelMessageID);
        if (!onMessageAt) continue;
        entries.push({
            kind: 'note',
            when: n.__mj_CreatedAt,
            channelName: '',
            channelType: '',
            who: commentAuthor?.(n) || n.AuthorEmail || 'Staff',
            content: n.Body || '',
            // No messageId here: the ticket screen keys reply edit/approve affordances on it.
            noteOnMessageAt: onMessageAt,
        });
    }

    return entries.sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime());
}
